import React, { useState, useEffect, useMemo } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { Navbar } from "../Navigation/Navbar";
import {
  DatePicker,
  Select,
  Space,
  Divider,
  BackTop,
  Popconfirm,
  Checkbox,
  Layout,
  InputNumber,
  Button,
  Table,
  Collapse,
} from "antd";
import moment from "moment";
import { DrillCharts } from "./DrillCharts";
import { DrillCompareTable } from "./DrillCompareTable";
import "../../Styles/styles-Compare.css";
import "../../Styles/styles-PostData.css";
import { DrillTable } from "./DrillTable";
import { useDispatch, useSelector } from "react-redux";
import { renderAsPDF } from "../../Utils/pdf_utils";
import {
  changeDateSelection,
  changeDrillSelection,
  changePositionSelection,
  getDrills,
  getDrillsBreakdownData,
  getDrillsCompareData,
  setDrillsBreakdownData,
} from "../../store/actions/drills.actions";
import { LoadingComponent } from "../Common/LoadingComponent";
import { HelpButton } from "../Common/HelpButton";
import { DataColorUtil } from "../../Utils/color_utils";

const { Footer } = Layout;
const { Panel } = Collapse;
const { Option, OptGroup } = Select;

export const Drills = () => {
  const dispatch = useDispatch();
  const [dataColorUtil, setDataColorUtil] = useState(null);

  const {
    breakdownData,
    compareData,
    drills,
    dates,
    loading,
    filter: { drill, date, position },
  } = useSelector(({ drills }) => drills);
  const {
    positions,
    sport,
    info: { hideDistanceZones, display_name: teamName },
    color_strategies: colorStrategies
  } = useSelector(({ team }) => team);

  const [drillCompare, setDrillCompare] = useState(false);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const searchParams = new URLSearchParams(window.location?.search);
      const searchDrill = searchParams.get("drill");
      const searchDate = searchParams.get("date");

      await dispatch(getDrills());

      if (searchDrill) {
        await dispatch(changeDrillSelection(searchDrill));
        await dispatch(changeDateSelection(searchDate));
        await dispatch(getDrillsBreakdownData());
      }

      await dispatch(getDrillsCompareData());
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (breakdownData?.colors && breakdownData?.metrics?.length && colorStrategies?.length) {
      setDataColorUtil(new DataColorUtil({
        teamName,
        gpsBaselines: breakdownData.colors,
        teamGpsMetrics: breakdownData.metrics,
        colorStrategies
      }));
    } else {
      setDataColorUtil(null);
    }
  }, [breakdownData?.colors, breakdownData?.metrics, teamName, colorStrategies]);

  const onDateChange = (date) => {
    dispatch(changeDateSelection(date));
  };

  const onDrillChange = (val) => {
    dispatch(changeDrillSelection(val));
  };

  const positionChange = (val) => {
    dispatch(changePositionSelection(val));
  };

  const onCheckboxChange = (e) => {
    setDrillCompare(e.target.checked);
  };

  const handleDownloadPDF = async () => {
    setRendering(true);

    let descString = `Date: ${date}`;
    if (position) descString = `${descString} | Position: ${position}`;

    await new Promise((resolve) => setTimeout(resolve, 300));

    const widget = null;
    const table = document.getElementsByTagName("table")[0];
    const chartDOMs = document.getElementsByClassName("example-table-graph");

    await renderAsPDF({header: descString, widget, columns: 1, table, chartDOMs});

    setRendering(false);
  };

  let position_block = null;
  if (sport === "soccer") {
    position_block = (
      <>
        <div>
          <Select
            allowClear
            showSearch
            placeholder="Choose Position"
            optionFilterProp="value"
            value={position}
            style={{ width: 200 }}
            onChange={positionChange}
          >
            <OptGroup label="Goalkeepers">
              <Option value="1">1</Option>
            </OptGroup>
            <OptGroup label="Defenders">
              <Option value="2/3">2/3</Option>
              <Option value="4/5">4/5</Option>
              <Option value="Defenders">All Defenders</Option>
            </OptGroup>
            <OptGroup label="Midfielders">
              <Option value="6">6</Option>
              <Option value="8/10">8/10</Option>
              <Option value="Midfielders">All Midfielders</Option>
            </OptGroup>
            <OptGroup label="Forwards">
              <Option value="7/11">7/11</Option>
              <Option value="9">9</Option>
              <Option value="Forwards">All Forwards</Option>
            </OptGroup>
          </Select>
        </div>
      </>
    );
  } else {
    position_block = (
      <div>
        <Select
          allowClear
          showSearch
          placeholder="Choose Position"
          optionFilterProp="value"
          value={position}
          style={{ width: 200 }}
          onChange={positionChange}
        >
          {positions.map((position, i) => {
            return <Option value={position}>{position}</Option>;
          })}
          ;
        </Select>
      </div>
    );
  }

  return (
    <LoadingComponent loading={loading}>
      <Navbar />
      <HelpButton page="Drills" />
      <div className="search-divider"></div>
      <Space>
        <Space>
          <Checkbox
            className=""
            checked={drillCompare}
            onChange={onCheckboxChange}
          >
            Compare Drills?
          </Checkbox>
          {drillCompare ? null : (
            <Select
              showSearch
              style={{ width: 200 }}
              placeholder="Choose Drill"
              value={drill}
              onChange={onDrillChange}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {drills.length !== 0 ? (
                drills.map((drill, i) => {
                  return (
                    <Option key={i} value={drill.name}>
                      {drill.name}
                    </Option>
                  );
                })
              ) : (
                <></>
              )}
            </Select>
          )}
        </Space>
        {drill !== undefined && drill !== null && dates.length === 0 ? (
          <p>You have not uploaded any data for this drill.</p>
        ) : (
          <></>
        )}
        {drillCompare === false && dates.length !== 0 ? (
          <Space>
            <Select
              showSearch
              style={{ width: 200 }}
              placeholder="Choose Date"
              value={date}
              onChange={onDateChange}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {dates.length !== 0 ? (
                dates.map((date, i) => {
                  return (
                    <Option key={i} value={date["date"]}>
                      {date["date"]}
                    </Option>
                  );
                })
              ) : (
                <></>
              )}
            </Select>
          </Space>
        ) : (
          <></>
        )}
        {drillCompare === false && date !== undefined ? position_block : <></>}
        <Button
          loading={rendering}
          type="primary"
          onClick={() => {
            handleDownloadPDF();
          }}
        >
          Export as PDF
				</Button>
      </Space>

      <div className="search-divider">
        <Divider />
      </div>

      <div className="drill-timeline-collapse">
        <Collapse className="">
          <Panel header="Show Drill Table">
            <div className="drill-color-table">
              <DrillTable dataIn={compareData} />
            </div>
          </Panel>
        </Collapse>
      </div>

      {drillCompare ? (
        <div>
          <div className="drill-compare-table">
            <DrillCompareTable
              set_data={(data) => {
                dispatch(setDrillsBreakdownData(data));
              }}
              drills={drills.map(({ name }) => name)}
            />
          </div>
        </div>
      ) : null}

      <div className="comparison-chart-compare">
        {breakdownData.length !== 0 ? (
          <DrillCharts
            dataIn={breakdownData}
            individual={!drillCompare}
            hideDistanceZones={hideDistanceZones}
            team_name={teamName}
            dataColorUtil={dataColorUtil}
          ></DrillCharts>
        ) : (
          <></>
        )}
      </div>

    </LoadingComponent>
  );
};
